/**
 * Preflight API - FREE corpus analysis before expensive unlock.
 */

import apiClient, { type RequestConfig } from './client';
import { isApiError } from './types';
import type {
  PreflightResult,
  PreflightSummary,
  UnlockEstimate,
  UnlockEstimateOptions,
} from './types';

const ENDPOINT = '/preflight';

export const preflightApi = {
  /**
   * Create a new preflight analysis request.
   */
  async create(
    matterId: string,
    userId?: string,
    config?: RequestConfig
  ): Promise<PreflightSummary> {
    const { data } = await apiClient.post<PreflightSummary>(ENDPOINT, {
      matter_id: matterId,
      user_id: userId || 'anonymous',
    }, config);
    return data;
  },

  /**
   * Start preflight analysis on uploaded files.
   * SEC-005: No longer accepts upload path from client.
   * The server resolves the path from the preflight record.
   */
  async start(
    preflightId: string,
    config?: RequestConfig
  ): Promise<{
    preflight_id: string;
    job_id: string;
    status: string;
    message: string;
  }> {
    const { data } = await apiClient.post<{
      preflight_id: string;
      job_id: string;
      status: string;
      message: string;
    }>(`${ENDPOINT}/${preflightId}/run`, null, config);
    return data;
  },

  /**
   * Build preflight from existing documents in a matter.
   * For matters where docs were uploaded through the ingestion pipeline.
   * Completes synchronously - no background job needed.
   */
  async analyzeFromMatter(
    matterId: string,
    config?: RequestConfig
  ): Promise<PreflightResult> {
    const { data } = await apiClient.post<PreflightResult>(
      `${ENDPOINT}/from-matter/${matterId}`,
      null,
      config
    );
    return data;
  },

  /**
   * Upload files and immediately start preflight.
   */
  async uploadAndAnalyze(
    matterId: string,
    files: File[],
    userId?: string,
    onProgress?: (progress: number) => void,
    config?: RequestConfig
  ): Promise<{
    preflight_id: string;
    job_id: string;
    files_received: number;
    status: string;
    message: string;
  }> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // SEC-008: Server no longer returns filesystem paths
    const { data } = await apiClient.upload<{
      preflight_id: string;
      job_id: string;
      files_received: number;
      status: string;
      message: string;
    }>(`${ENDPOINT}/upload-and-analyze?matter_id=${matterId}&user_id=${userId || 'anonymous'}`, formData, onProgress, config);

    return data;
  },

  /**
   * Get preflight status and results.
   */
  async get(preflightId: string, config?: RequestConfig): Promise<PreflightResult> {
    const { data } = await apiClient.get<PreflightResult>(`${ENDPOINT}/${preflightId}`, config);
    return data;
  },

  /**
   * List all preflights for a matter.
   */
  async listForMatter(matterId: string, config?: RequestConfig): Promise<PreflightSummary[]> {
    const { data } = await apiClient.get<PreflightSummary[]>(`${ENDPOINT}/matter/${matterId}`, config);
    return data;
  },

  /**
   * Get the latest preflight for a matter.
   */
  async getLatest(matterId: string, config?: RequestConfig): Promise<PreflightResult | null> {
    const { data } = await apiClient.get<PreflightResult | null>(`${ENDPOINT}/matter/${matterId}/latest`, config);
    return data;
  },

  /**
   * Get the latest preflight for a matter, treating missing data as "not created yet".
   *
   * Kept as a compatibility wrapper because older API deployments returned 404
   * before the endpoint was normalized to return null.
   */
  async getLatestOrNull(
    matterId: string,
    config?: RequestConfig
  ): Promise<PreflightResult | null> {
    try {
      return await this.getLatest(matterId, config)
    } catch (error) {
      if (isApiError(error) && error.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Delete a preflight record.
   */
  async delete(preflightId: string, config?: RequestConfig): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${preflightId}`, config);
  },

  /**
   * Get unlock cost estimate with options.
   */
  async estimateUnlock(
    preflightId: string,
    options?: UnlockEstimateOptions,
    config?: RequestConfig
  ): Promise<UnlockEstimate> {
    const { data } = await apiClient.post<UnlockEstimate>(
      `${ENDPOINT}/${preflightId}/estimate-unlock`,
      options || {},
      config
    );
    return data;
  },

  /**
   * Poll for preflight completion.
   * Returns when status is 'completed' or 'failed'.
   */
  async waitForCompletion(
    preflightId: string,
    onProgress?: (preflight: PreflightResult) => void,
    pollInterval = 2000,
    timeout = 300000 // 5 minutes
  ): Promise<PreflightResult> {
    const startTime = Date.now();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const preflight = await this.get(preflightId);

      if (onProgress) {
        onProgress(preflight);
      }

      if (preflight.status === 'completed' || preflight.status === 'failed') {
        return preflight;
      }

      if (Date.now() - startTime > timeout) {
        throw new Error('Preflight analysis timed out');
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  },
};

export default preflightApi;
